import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectorRef, Component, computed, effect, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LocaleService } from '@core/services/locale.service';
import { PermissionService } from '@core/services/permission.service';
import { SidebarService } from '@core/services/sidebar.service';
import { AuthStore } from '@core/store/core/auth.store';
import { ResponsiveService, ThemeService } from '@mmscan/design-system';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filterMenuItemsArray } from '@shared/utils/menu-item.utils';
import { type MenuItem, PrimeIcons } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { MenubarModule } from 'primeng/menubar';
import { LogoutButtonComponent } from './logout-button/logout-button.component';
import { NotificationComponent } from './notification/notification.component';
import { SettingsButtonsComponent } from './settings-buttons/settings-buttons.component';

const NAVBAR_RESOURCES = [
  "micare",
  "access_control",
  "message_board_post",
] as const;

@Component({
  selector: "mp-navbar",
  imports: [
    NgOptimizedImage,
    MenubarModule,
    MenuModule,
    ButtonModule,
    CommonModule,
    SettingsButtonsComponent,
    NotificationComponent,
    LogoutButtonComponent,
    RouterModule,
    TranslatePipe,
  ],
  templateUrl: "./navbar.component.html",
})
export class NavbarComponent {
  logoUrl = "logos/logo-miportal-white.svg";

  items: MenuItem[] | undefined;

  responsiveService = inject(ResponsiveService);
  protected authStore = inject(AuthStore);
  protected sidebarService = inject(SidebarService);
  private permissionService = inject(PermissionService);
  private themeService = inject(ThemeService);
  private router = inject(Router);
  private cdRef = inject(ChangeDetectorRef);
  private translate = inject(TranslateService);
  private localeService = inject(LocaleService);

  /** True once all navbar resources are present in the permission cache */
  private readonly navResourcesLoaded = computed(() =>
    NAVBAR_RESOURCES.every((r) => r in this.authStore.featuresByResource()),
  );

  constructor() {
    effect(() => {
      this.logoUrl =
        this.themeService.theme() === "dark"
          ? "logos/logo-miportal-white.svg"
          : "logos/logo-miportal-black.svg";
    });

    // Trigger loading of navbar resources once the user is authenticated
    effect(() => {
      if (this.authStore.isLoggedIn()) {
        void this.authStore.loadFeaturesByResources(NAVBAR_RESOURCES);
      }
    });

    // Rebuild menu items whenever navbar resources become available or the language changes
    effect(() => {
      this.navResourcesLoaded();
      this.localeService.locale();

      // Build menu items in a microtask to avoid ExpressionChangedAfterItHasBeenCheckedError
      Promise.resolve().then(() => {
        this.buildMenuItems();
        // Ensure change detection runs after the async update so we don't leave the view stale
        this.cdRef.detectChanges();
      });
    });
  }

  private buildMenuItems(): void {
    this.items = [
      {
        label: this.translate.instant('nav.menu.adminTools'),
        icon: PrimeIcons.DATABASE,
        items: [
          {
            label: this.translate.instant('nav.menu.accessControl'),
            icon: PrimeIcons.USERS,
            command: () => {
              this.router.navigate(["/access-control"]);
            },
            canAccess: this.permissionService.canList("access_control"),
          },
          {
            label: this.translate.instant("nav.menu.messageBoard"),
            icon: PrimeIcons.COMMENTS,
            command: () => {
              this.router.navigate(["/message-board"]);
            },
            canAccess: this.permissionService.canList("message_board_post"),
          },
        ],
      },
      {
        label: this.translate.instant('nav.menu.internalTools'),
        icon: PrimeIcons.TH_LARGE,
        permission: "view:internal-tools",
        items: [
          {
            label: "MiCare",
            icon: PrimeIcons.BOOK,
            canAccess: this.permissionService.canList("micare"),
            command: () => {
              this.router.navigate(["/micare"]);
            },
          },
          {
            label: "MiRide",
            icon: "fa-solid fa-car-side",
            command: () => {
              this.router.navigate(["/miride"]);
            },
          },
        ],
      },
      {
        label: this.translate.instant('nav.menu.dealerTools'),
        icon: PrimeIcons.BRIEFCASE,
        items: [
          {
            label: this.translate.instant('nav.menu.miTechForum'),
            icon: 'fa-solid fa-people-arrows',
          },
        ],
      },
      {
        label: this.translate.instant('nav.menu.externalLinks'),
        permission: 'view:external-links',
        icon: PrimeIcons.LINK,
        items: [
          {
            label: "Mitsubishi Dealer Link",
            icon: "fa-solid fa-building-shield",
            url: "https://mdl.mmsa.com/Extranet/index.htm"
          },
          {
            label: "GoFarAcademy",
            icon: "fa-solid fa-graduation-cap",
            url: "https://www.gofaracademy.ca/account/awslogin",
          },
          {
            label: "MiShowroom",
            icon: "fa-solid fa-store",
            url: "https://www.miadvantage.ca/mishowroom/redirect",
          },
          {
            label: "MiServiceView",
            icon: "fa-solid fa-car",
            url: "https://serviceview-mmscan.na.urbansciencesolutions.com",
          },
          {
            label: this.translate.instant("nav.menu.midelivery"),
            icon: "fa-solid fa-truck-fast",
            url: "https://www.miadvantage.ca/redirect",
          }
        ],
      },
    ];

    this.items = filterMenuItemsArray(this.items);
  }
}
